import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { monthsUntilOccasionalMonth } from '../../utils/occasionalMonthlyEstimate';
import { cn } from '../../utils/cn';

function windowOffset(itemMonth, currentMonth) {
  return (itemMonth - currentMonth + 12) % 12;
}

function windowYear(item, currentMonth, referenceYear) {
  if (item.target_year != null) return item.target_year;
  return item.month >= currentMonth ? referenceYear : referenceYear + 1;
}

function itemTargetDate(item, currentMonth, referenceYear) {
  const targetYear = windowYear(item, currentMonth, referenceYear);
  return new Date(targetYear, item.month - 1, item.day_of_month || 1, 12, 0, 0);
}

function daysUntilPayment(item, currentMonth, referenceYear) {
  const target = itemTargetDate(item, currentMonth, referenceYear);
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

/** ds/card/annual-agenda no .pen — 1 cartão com faixa de 12 meses, não N cópias idênticas. */
export function AnnualAgendaCard({ items: allItems = [], referenceYear, currentMonth, className }) {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  // target_year explícito e já passado = instância pontual concluída, não entra na agenda.
  const items = allItems.filter((item) => itemTargetDate(item, currentMonth, referenceYear) >= today);

  if (!items.length) return null;

  const copy = pt.dashboard.annualAgenda;
  const monthAbbr = pt.dashboard.monthAbbr;

  const sorted = [...items].sort((a, b) => {
    const oa = windowOffset(a.month, currentMonth);
    const ob = windowOffset(b.month, currentMonth);
    if (oa !== ob) return oa - ob;
    return (a.day_of_month || 1) - (b.day_of_month || 1);
  });
  const rows = sorted.slice(0, 3);
  const total = items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const untilMonth = rows[rows.length - 1].month;

  const buckets = Array.from({ length: 12 }, (_, i) => {
    const m = ((currentMonth - 1 + i) % 12) + 1;
    const bucketTotal = items
      .filter((item) => item.month === m)
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
    return { month: m, total: bucketTotal };
  });
  const maxBucket = Math.max(...buckets.map((b) => b.total), 1);

  const nextItem = sorted[0];
  const nextDays = daysUntilPayment(nextItem, currentMonth, referenceYear);
  const footLeft =
    nextDays <= 0
      ? copy.footLeftToday
      : nextDays === 1
        ? copy.footLeftTomorrow
        : copy.footLeft.replace('{days}', String(nextDays));

  return (
    <div
      className={cn(
        'flex flex-col gap-space-4 rounded-ds-lg border border-border bg-card p-space-5 shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]',
        className,
      )}
    >
      <div className="flex flex-col gap-space-1">
        <p className="type-section text-muted-foreground">{copy.eyebrow}</p>
        <p className="font-display text-type-display-metric font-bold tracking-[-0.017em] text-card-foreground">
          {formatEUR(total)}
        </p>
        <p className="text-type-label font-normal text-muted-foreground">
          {items.length === 1
            ? copy.subOne.replace('{month}', monthAbbr[untilMonth])
            : copy.sub.replace('{count}', String(items.length)).replace('{month}', monthAbbr[untilMonth])}
        </p>
      </div>

      <div className="flex flex-col gap-space-2">
        <div className="flex h-11 items-end gap-space-1">
          {buckets.map((b, i) => {
            const height = b.total > 0 ? Math.round(12 + (b.total / maxBucket) * 32) : 6;
            return (
              <div
                key={`${b.month}-${i}`}
                className={cn(
                  'w-full rounded-sm',
                  b.total > 0 ? 'bg-sage-600' : 'bg-sparkline-bar',
                )}
                style={{ height: `${height}px` }}
              />
            );
          })}
        </div>
        <div className="flex items-center justify-between text-type-label font-normal text-muted-foreground">
          <span>
            {monthAbbr[currentMonth].toLowerCase()} {String(referenceYear).slice(-2)}
          </span>
          <span>
            {monthAbbr[buckets[11].month].toLowerCase()}{' '}
            {String(currentMonth === 1 ? referenceYear : referenceYear + 1).slice(-2)}
          </span>
        </div>
      </div>

      <div className="flex flex-col">
        {rows.map((item, index) => (
          <div key={item.id}>
            <div className="flex items-center gap-space-3 py-space-2">
              <span className="flex h-7 w-11 shrink-0 items-center justify-center rounded-ds-sm bg-sage-tint">
                <span className="text-type-chip font-bold uppercase tracking-[0.02em] text-sage-on-tint">
                  {monthAbbr[item.month]}
                </span>
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-type-body-sm font-semibold text-card-foreground">
                  {item.name}
                </p>
                <p className="text-type-chip font-normal text-muted-foreground">
                  {item.day_of_month} {monthAbbr[item.month]} ·{' '}
                  {(() => {
                    const months = monthsUntilOccasionalMonth(
                      item.month,
                      new Date(),
                      windowYear(item, currentMonth, referenceYear),
                    );
                    return months === 1
                      ? copy.relativeMonth
                      : copy.relativeMonths.replace('{count}', String(months));
                  })()}
                </p>
              </div>
              <span className="shrink-0 font-display text-type-display-row font-semibold text-card-foreground">
                {formatEUR(Number(item.amount))}
              </span>
            </div>
            {index < rows.length - 1 ? <div className="h-px w-full bg-border" /> : null}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-space-2 border-t border-border pt-space-3 text-type-label">
        <span className="font-normal text-muted-foreground">{footLeft}</span>
        <span className="font-semibold text-card-foreground">
          {formatEUR(Number(nextItem.amount))}
        </span>
      </div>
    </div>
  );
}

export default AnnualAgendaCard;
